/**
 * useImageHandling カスタムフック
 *
 * 配信フォームの画像処理ロジックを共通化するフック。
 * normalForm と segmentForm で共有される画像関連の状態管理と処理を提供。
 *
 * @file 画像処理共通フック
 * @date 2025/10/01
 */

import { useState } from 'react';
import { RcFile } from 'antd/es/upload';
import { BroadCastFormType } from 'src/models/broadCast';

/**
 * useImageHandling のパラメータ
 */
interface UseImageHandlingParams {
  /** 編集時の初期値 */
  init?: BroadCastFormType;
  /** 送信内容タイプ */
  type?: 'text' | 'image' | 'template' | 'textAndImage' | 'textAndTemplate';
}

/**
 * useImageHandling の戻り値
 */
interface UseImageHandlingReturn {
  /** ユーザーがアップロードした画像ファイル */
  inputImage?: RcFile;
  /** テンプレートから設定された画像URL */
  templateImageUrl: string | null;
  /** 既存の画像URLを取得 */
  getExistingImageUrl: () => string | null;
  /** 画像変更時の処理 */
  handleImageChange: (file?: RcFile) => void;
  /** テンプレート画像クリア処理 */
  handleTemplateImageClear: () => void;
  /** inputImage の直接設定 */
  setInputImage: (file?: RcFile) => void;
  /** templateImageUrl の直接設定 */
  setTemplateImageUrl: (url: string | null) => void;
}

/**
 * 画像処理共通フック
 *
 * normalForm と segmentForm で共通する画像処理ロジックを提供。
 * テンプレート画像とユーザーアップロード画像の管理を行う。
 *
 * @param params - フックのパラメータ
 * @returns 画像処理の状態と関数
 */
export const useImageHandling = ({
  init,
  type,
}: UseImageHandlingParams): UseImageHandlingReturn => {
  // 状態管理
  const [inputImage, setInputImage] = useState<RcFile | undefined>();
  const [templateImageUrl, setTemplateImageUrl] = useState<string | null>(null);

  // 送信内容タイプに応じた表示制御
  const _needImage = type === 'image' || type === 'textAndImage';
  const _needTemplate = type === 'template' || type === 'textAndTemplate';

  /**
   * 既存の画像URLを取得
   */
  const getExistingImageUrl = (): string | null => {
    if (_needImage) {
      return init?.broadCast?.message.imageUrl || null;
    }
    if (_needTemplate) {
      return init?.broadCast?.message.tempImageUrl || null;
    }
    return null;
  };

  /**
   * 画像変更時の処理
   * ユーザーがアップロードした画像ファイルを状態に保存
   *
   * @param file - アップロードされた画像ファイル
   */
  const handleImageChange = (file?: RcFile) => {
    setInputImage(file);
  };

  /**
   * テンプレート画像クリア処理
   * テンプレートから設定された画像URLをクリア
   */
  const handleTemplateImageClear = () => {
    setTemplateImageUrl(null);
  };

  return {
    inputImage,
    templateImageUrl,
    getExistingImageUrl,
    handleImageChange,
    handleTemplateImageClear,
    setInputImage,
    setTemplateImageUrl,
  };
};